/**
 * Filters job locations for display in hit cards.
 * 
 * Rules:
 * - If there's at least 1 location in Finland, only show:
 *   - Finland locations (e.g., "Helsinki, Finland")
 *   - Remote regional locations (e.g., "Remote - Global", "Remote - Europe")
 * - Don't show non-Finland city locations (e.g., "Berlin, Germany") - users can see these when clicking into the job
 * 
 * @param locations - Array of location strings (with countries, e.g., "Helsinki, Finland")
 * @returns Filtered array of location strings
 */

import {
  isFinlandLocationStrict,
  isRemoteRegionalScopeForJobCard,
} from '../geocoding/is-finland-location';

/**
 * Filters locations based on the business rules:
 * - If there's at least 1 Finland location, only show Finland locations and remote regional locations
 * - Otherwise, show all locations
 */
export function filterJobLocations(locations: (string | undefined | null)[]): string[] {
  if (!locations || locations.length === 0) return [];
  
  // Filter out null/undefined and convert to strings
  const validLocations = locations
    .filter((loc): loc is string => typeof loc === 'string' && loc.trim() !== '')
    .map(loc => loc.trim());
  
  if (validLocations.length === 0) return [];
  
  // Check if there's at least one Finland location
  const hasFinlandLocation = validLocations.some(loc => isFinlandLocationStrict(loc));
  
  if (hasFinlandLocation) {
    // Only show Finland locations and remote regional locations
    return validLocations.filter(
      (loc) => isFinlandLocationStrict(loc) || isRemoteRegionalScopeForJobCard(loc),
    );
  }
  
  // If no Finland locations, show all locations
  return validLocations;
}
